import {
  switchMap,
  mergeMap,
  filter,
  catchError,
  withLatestFrom,
  debounceTime
} from 'rxjs/operators'
import { of } from 'rxjs'
import Actions from '../actions'
import { getJSON, getJSONToken, getPDF, postJSON } from '../utils/fetchUtils'
import { catchAndDispatchCustomError } from '../utils/customErrorHandler'
import { getPayload } from '../utils/rxUtils'
import { setUrlParams } from '../utils/useParamUtil'
import { event, eventTypes } from '../consts/eventConsts'
import {
  backendUrl,
  providersBackendUrl,
  DEFAULT_PAGE,
  MAX_IN_PAGE
} from '../consts'
import { ProviderPCPTypes } from '../consts/ProviderConsts'
import {
  getLocationParams,
  getFilterParams,
  getPcpParams
} from '../utils/providerSearchUtils'
import {
  appendDirectoryAccessSource,
  directoryAccessParams
} from '../utils/directoryAccessUtils'
import { uniqBy } from 'lodash'
import $t from '../i18n'

export const providersSearch = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PROVIDERS_REQUESTED),
    withLatestFrom(state$),
    switchMap(([payload, { providers }]) => {
      const providerToken = providers?.providerToken
      return getJSONToken(
        `/providers/search-provider/?${setUrlParams(payload)}`,
        backendUrl,
        providerToken
      ).pipe(
        mergeMap(providers => {
          // send analytics event in case of empty results
          if (!providers?.count) {
            return of(
              Actions.createAction(Actions.PROVIDERS_RECEIVED, providers),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data: payload,
                element: 'Provider empty results'
              })
            )
          }
          return of(Actions.createAction(Actions.PROVIDERS_RECEIVED, providers))
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const providersPrint = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PROVIDERS_PRINT_REQUESTED),
    withLatestFrom(state$),
    switchMap(([payload, { providers }]) => {
      const providerToken = providers?.providerToken
      return getPDF(
        `/providers/search-provider-pdf/?${setUrlParams(payload)}&key=${
          providers?.providersResultsKey
        }`,
        backendUrl,
        providerToken
      ).pipe(
        mergeMap(providers_pdf => {
          const blob = providers_pdf
          // Check if we're in React Native WebView
          const isReactNativeWebView =
            typeof window.ReactNativeWebView?.postMessage === 'function'

          if (isReactNativeWebView) {
            // React Native WebView - send message to RN
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                message: 'provider-print',
                payload: payload,
                providersResultsKey: providers?.providersResultsKey
              })
            )
          } else {
            // Mobile Safari or other browsers - use original approach
            const isSafariMobile =
              /iPhone|iPad|iPod/i.test(navigator.userAgent) &&
              /Safari/i.test(navigator.userAgent)

            if (isSafariMobile) {
              const button = document.createElement('a')
              button.href = blob
              button.download = 'providers.pdf'
              document.body.appendChild(button)
              button.click()
              document.body.removeChild(button)
            } else {
              window.open(blob)
            }
          }
          return of(Actions.createAction(Actions.PROVIDERS_PRINT_RECEIVED))
        }),
        catchAndDispatchCustomError
      )
    })
  )
export const providersNameList = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PROVIDER_NAME_LIST_REQUESTED),
    debounceTime(300),
    withLatestFrom(state$),
    switchMap(([payload, { providers }]) => {
      const providerToken = providers?.providerToken
      const networks = payload?.networks?.join(',')
      const name = payload?.name
      const params = new URLSearchParams({ networks, name })
      return getJSONToken(
        `/providers/get-provider-names?${params.toString()}`,
        backendUrl,
        providerToken
      ).pipe(
        mergeMap(res => {
          return of(
            Actions.createAction(
              Actions.PROVIDER_NAME_LIST_RECEIVED,
              uniqBy(
                res?.provider_list_names?.map(item => ({
                  label: item?.[0],
                  value: item?.[0]
                })),
                'value'
              )
            )
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const SecialityList = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SPECIALITY_LIST_REQUESTED),
    withLatestFrom(state$),
    switchMap(([payload, { providers }]) => {
      const providerToken = providers?.providerToken
      const providerName = payload?.providerName || ''
      return getJSONToken(
        `/providers/search-specialities/?providerName=${providerName}`,
        backendUrl,
        providerToken
      ).pipe(
        mergeMap(providers => {
          return of(
            Actions.createAction(Actions.SPECIALITY_LIST_RECEIVED, providers)
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getProvider = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PROVIDER_REQUESTED),
    withLatestFrom(state$),
    switchMap(([payload, { providers }]) => {
      const providerToken = providers?.providerToken
      const providerId = payload?.id
      return getJSONToken(
        `/providers/get-provider/?providerId=${providerId}`,
        backendUrl,
        providerToken
      ).pipe(
        mergeMap(provider => {
          return of(
            Actions.createAction(Actions.PROVIDER_RECEIVED, provider?.[0]),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: payload,
              element: 'Provider selected'
            })
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

const serializePCP = (pcpProviders, pcpHistory, pcpList) => {
  // Merge pcpList and pcpHistory into one array
  const allPcps = [
    ...(pcpList || []),
    ...(pcpHistory?.provider_accepting_address && pcpHistory?.end_date
      ? [pcpHistory]
      : [])
  ]

  // Create map from merged array, keyed by provider_accepting_address
  const pcpMap = allPcps.reduce((acc, pcp) => {
    // Only add or update if not already in map, or if this has an end_date (historical)
    if (!acc[pcp.provider_accepting_address] || pcp.end_date) {
      acc[pcp.provider_accepting_address] = {
        pcp_type: pcp.pcp_type || 'physical',
        ...(pcp.start_date && { start_date: pcp.start_date }),
        ...(pcp.end_date && { end_date: pcp.end_date })
      }
    }
    return acc
  }, {})

  // Filter and enrich providers by matching provider.id with provider_accepting_address
  return pcpProviders?.reduce((acc, provider) => {
    const pcpInfo = pcpMap?.[provider?.id]
    if (pcpInfo) {
      acc.push({
        ...provider,
        pcp_type: pcpInfo.pcp_type,
        telehealth: pcpInfo.pcp_type === 'telehealth',
        ...(pcpInfo.start_date && { start_date: pcpInfo.start_date }),
        ...(pcpInfo.end_date && { end_date: pcpInfo.end_date })
      })
    }
    return acc
  }, [])
}

export const careTeamListEpic = action$ =>
  action$.pipe(
    getPayload(Actions.PROVIDER_PCP_REQUESTED),
    filter(payload => payload?.patientId),
    switchMap(payload => {
      const patientId = payload?.patientId
      return getJSON(`/providers/get-patient-pcp/?patientId=${patientId}`).pipe(
        switchMap(res =>
          of(
            Actions.createAction(Actions.PROVIDER_PCP_RECEIVED, {
              pcpByType: res?.pcp,
              pcpProviders: serializePCP(
                res?.providers,
                res?.pcp_history,
                res?.pcp
              ),
              pcp_telehealth_flag: res?.pcp_telehealth_flag,
              patientId
            })
          )
        ),
        catchAndDispatchCustomError
      )
    })
  )

export const updatePCPEpic = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_PCP_REQUESTED),
    switchMap(({ providerId, pcpType, patientId }) => {
      return postJSON(`/providers/update-patient-pcp/`, {
        patientId,
        providerId,
        pcpType
      }).pipe(
        switchMap(() => {
          const actions = [
            Actions.createAction(Actions.UPDATE_PCP_RECEIVED, {
              pcpType,
              patientId
            }),
            Actions.createAction(Actions.PROVIDER_PCP_REQUESTED, { patientId }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: { providerId, pcpType, patientId },
              element: 'Update pcp'
            })
          ]
          if (pcpType === ProviderPCPTypes.TELEHEALTH) {
            actions.push(Actions.createAction(Actions.USER_PROFILE_REQUESTED))
          }
          return of(...actions)
        }),
        catchError(e => {
          return of(
            Actions.createAction(Actions.UPDATE_PCP_FAILED, {
              reason: 'pcpUpdateError ' + e
            })
          )
        })
      )
    })
  )

export const pcpUpdatePopMessage = action$ =>
  action$.pipe(
    getPayload(Actions.UPDATE_PCP_RECEIVED),
    switchMap(({ pcpType }) =>
      of(
        Actions.createAction(Actions.MESSAGE_DISPLAYED, {
          content:
            pcpType === 'telehealth'
              ? $t('alerts.pcpTelehealthUpdateCompleted')
              : $t('alerts.pcpUpdateCompleted'),
          type: 'success'
        })
      )
    )
  )

export const providerSearchEpic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PROVIDERS_SEARCH_REQUESTED),
    withLatestFrom(state$),
    switchMap(
      ([
        {
          query,
          providersSearchLocation = {},
          providersSearchFilters = {},
          sortBy = null,
          page = DEFAULT_PAGE,
          per_page = MAX_IN_PAGE
        },
        state
      ]) => {
        if (!query) return of()

        const patientsPcpProviders = state.profile?.patientsPcpProviders || []

        const telehealthPcps = getPcpParams(
          patientsPcpProviders,
          ProviderPCPTypes.TELEHEALTH
        )
        const physicalPcps = getPcpParams(
          patientsPcpProviders,
          ProviderPCPTypes.PHYSICAL
        )

        const params = new URLSearchParams({
          query,
          ...getLocationParams(providersSearchLocation),
          ...getFilterParams(providersSearchFilters),
          page: page.toString(),
          per_page: per_page.toString(),
          ...(sortBy && { sort_by: sortBy }),
          ...(telehealthPcps && { telehealthAssignedPcp: telehealthPcps }),
          ...(physicalPcps && { physicalAssignedPcp: physicalPcps }),
          ...directoryAccessParams()
        })

        return getJSON(
          `/search?${params.toString()}`,
          providersBackendUrl
        ).pipe(
          mergeMap(res =>
            of(
              Actions.createAction(
                Actions.PROVIDERS_SEARCH_RESULTS_RECEIVED,
                res
              )
            )
          ),
          catchError(e =>
            of(
              Actions.createAction(Actions.PROVIDERS_SEARCH_FAILED, {
                reason: 'searchProvidersError' + e
              })
            )
          )
        )
      }
    )
  )

export const searchProvidersPDF = action$ =>
  action$.pipe(
    getPayload(Actions.SEARCH_PROVIDERS_PDF_REQUESTED),
    switchMap(payload => {
      const {
        query,
        providersSearchLocation = {},
        providersSearchFilters = {},
        sortBy = null
      } = payload

      // Build URL parameters like the search API (no pagination for printing)
      const params = new URLSearchParams({
        query,
        ...getLocationParams(providersSearchLocation),
        ...getFilterParams(providersSearchFilters),
        ...(sortBy && { sort_by: sortBy }),
        ...directoryAccessParams()
      })

      return getPDF(
        `/search/pdf?${params.toString()}`,
        providersBackendUrl
      ).pipe(
        mergeMap(providers_pdf => {
          const blob = providers_pdf
          // Check if we're in React Native WebView
          const isReactNativeWebView =
            typeof window.ReactNativeWebView?.postMessage === 'function'

          if (isReactNativeWebView) {
            // React Native WebView - send message to RN
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                message: 'search-provider-pdf',
                payload: payload
              })
            )
          } else {
            // Mobile Safari or other browsers - use original approach
            const isSafariMobile =
              /iPhone|iPad|iPod/i.test(navigator.userAgent) &&
              /Safari/i.test(navigator.userAgent)

            if (isSafariMobile) {
              const button = document.createElement('a')
              button.href = blob
              button.download = 'search-providers.pdf'
              document.body.appendChild(button)
              button.click()
              document.body.removeChild(button)
            } else {
              window.open(blob)
            }
          }
          return of(Actions.createAction(Actions.SEARCH_PROVIDERS_PDF_RECEIVED))
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const providerSuggestionsEpic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PROVIDERS_SUGGESTIONS_REQUESTED),
    withLatestFrom(state$),
    switchMap(([{ query, providersSearchLocation = {} }, { providers }]) => {
      if (!query) return of()

      const params = new URLSearchParams({
        query,
        ...getLocationParams(providersSearchLocation),
        distanceRadius: providers.distanceFilterDefinition.default.toString(),
        year: providers.providersSearchFilters.year || '2025',
        ...directoryAccessParams()
      })

      return getJSON(
        `/suggestions?${params.toString()}`,
        providersBackendUrl
      ).pipe(
        mergeMap(res =>
          of(
            Actions.createAction(
              Actions.PROVIDERS_SUGGESTIONS_RESULTS_RECEIVED,
              res
            )
          )
        ),
        catchError(e =>
          of(
            Actions.createAction(Actions.PROVIDERS_SUGGESTIONS_FAILED, {
              reason: 'suggestionsError' + e
            })
          )
        )
      )
    })
  )

export const getProviderDetails = action$ =>
  action$.pipe(
    getPayload(Actions.PROVIDER_DETAILS_REQUESTED),
    switchMap(({ id }) => {
      if (!id) return of()
      return getJSON(
        appendDirectoryAccessSource(`/provider/${id}`),
        providersBackendUrl
      ).pipe(
        mergeMap(res =>
          of(Actions.createAction(Actions.PROVIDER_DETAILS_RECEIVED, res))
        ),
        catchAndDispatchCustomError
      )
    })
  )
